import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Plus, LayoutGrid, List } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { firestore, storage } from '@/lib/firebase';
import { collection, doc, getDocs, deleteDoc, updateDoc, query, orderBy, where } from 'firebase/firestore';
import { ref } from 'firebase/storage';
import { format } from 'date-fns';
import { ja, enUS, arSA } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Content, Conversation } from '@shared/types';
import { HistoryConversation } from '@/types/misc';
import { ConversationCard } from '@/components/history/ConversationCard';
import { VisualCard } from '@/components/history/VisualCard';
import { RenameDialogDrawer } from '@/components/history/RenameDialogDrawer';
import { useAuth } from '@/contexts/AuthContext';
import { getConversationAssetRoot } from '@/lib/firebaseAssetPaths';
import { deleteStorageFolderRecursively } from '@/lib/firebaseStorage';

export function HistoryView() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation('common');
  const dateFnsLocale = i18n.language?.startsWith('ja')
    ? ja
    : i18n.language?.startsWith('ar')
      ? arSA
      : enUS;
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'visual'>('list');
  const [editingConversation, setEditingConversation] =
    useState<Conversation | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (!open) {
      setEditingConversation(null);
    }
  };

  const conversationQuery = useQuery<HistoryConversation[]>({
    queryKey: ['conversations'],
    enabled: !!user?.id,
    queryFn: async () => {
      const convsCol = collection(firestore, 'conversations');
      const q = query(
        convsCol,
        where('user_id', '==', user?.id ?? ''),
        orderBy('updated_at', 'desc'),
      );
      const snap = await getDocs(q);
      const conversations = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Conversation[];

      const formattedConversations = await Promise.all(
        conversations.map(async (conv) => {
          // Get first message and message count
          const msgsCol = collection(firestore, 'conversations', conv.id, 'messages');
          const msgsSnap = await getDocs(query(msgsCol, orderBy('created_at', 'asc')));
          const messageCount = msgsSnap.size;
          const firstMsg = msgsSnap.docs[0]?.data();
          const rawContent = firstMsg?.content;
          const firstMessageContent =
            typeof rawContent === 'object' && rawContent !== null
              ? (rawContent as Content)
              : { text: '' };

          const formattedFirstMessage = {
            text: firstMessageContent.text ?? '',
            images: firstMessageContent.images ?? [],
          };

          return {
            ...conv,
            created_at: conv.created_at || new Date().toISOString(),
            updated_at: conv.updated_at || conv.created_at || new Date().toISOString(),
            message_count: messageCount,
            first_message: formattedFirstMessage as Content,
          };
        }),
      );

      return formattedConversations;
    },
  });

  useEffect(() => {
    if (conversationQuery.isError) {
      toast({
        title: t('toast.error'),
        description: t('history.loadFailed'),
        variant: 'destructive',
      });
    }
  }, [conversationQuery.isError, toast, t]);

  const deleteConversation = useMutation({
    mutationFn: async (conversationId: string) => {
      // Delete all messages in subcollection
      const msgsCol = collection(firestore, 'conversations', conversationId, 'messages');
      const msgsSnap = await getDocs(msgsCol);
      await Promise.all(msgsSnap.docs.map((d) => deleteDoc(d.ref)));

      // Delete conversation document
      await deleteDoc(doc(firestore, 'conversations', conversationId));

      // Clean up storage images
      if (user?.id) {
        try {
          const storageRef = ref(
            storage,
            getConversationAssetRoot(user.id, conversationId),
          );
          await deleteStorageFolderRecursively(storageRef);
        } catch {
          // Storage cleanup is best-effort
        }
      }
    },
    onMutate: async (conversationId) => {
      await queryClient.cancelQueries({ queryKey: ['conversations'] });
      const previousConversations = queryClient.getQueryData(['conversations']);
      queryClient.setQueryData(
        ['conversations'],
        (old: HistoryConversation[]) =>
          old.filter((conv) => conv.id !== conversationId),
      );
      return { previousConversations };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast({
        title: t('toast.success'),
        description: t('history.deleted'),
      });
    },
    onError: (error: unknown, _conversationId: string, context) => {
      console.error('Error deleting conversation:', error);
      queryClient.setQueryData(
        ['conversations'],
        context?.previousConversations,
      );
      toast({
        title: t('toast.error'),
        description: t('history.deleteFailed'),
        variant: 'destructive',
      });
    },
  });

  const renameConversation = useMutation({
    mutationFn: async ({
      conversationId,
      newTitle,
    }: {
      conversationId: string;
      newTitle: string;
    }) => {
      const convRef = doc(firestore, 'conversations', conversationId);
      await updateDoc(convRef, { title: newTitle });
    },
    onMutate: async ({ conversationId, newTitle }) => {
      await queryClient.cancelQueries({ queryKey: ['conversations'] });
      const previousConversations = queryClient.getQueryData(['conversations']);
      queryClient.setQueryData(
        ['conversations'],
        (old: HistoryConversation[]) =>
          old.map((conv) =>
            conv.id === conversationId ? { ...conv, title: newTitle } : conv,
          ),
      );
      return { previousConversations };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast({
        title: t('toast.success'),
        description: t('history.renamed'),
      });
      setEditingConversation(null);
      setOpen(false);
    },
    onError: (error: unknown, _variables, context) => {
      console.error('Error renaming conversation:', error);
      queryClient.setQueryData(
        ['conversations'],
        context?.previousConversations,
      );
      toast({
        title: t('toast.error'),
        description: t('history.renameFailed'),
        variant: 'destructive',
      });
    },
  });

  const handleRename = () => {
    if (!editingConversation) return;
    if (!newTitle.trim()) {
      toast({
        title: t('history.titleEmpty'),
        variant: 'default',
      });
      return;
    }
    renameConversation.mutate({
      conversationId: editingConversation.id,
      newTitle: newTitle.trim(),
    });
  };

  const filteredConversations =
    conversationQuery.data?.filter((conv: HistoryConversation) => {
      const messageText = conv.first_message.text ?? '';
      const title = conv.title?.toLowerCase() ?? '';
      const searchTerm = searchQuery.toLowerCase();

      return (
        title.includes(searchTerm) ||
        messageText.toLowerCase().includes(searchTerm)
      );
    }) ?? [];

  const groupConversationsByDate = () => {
    const groups: { [key: string]: HistoryConversation[] } = {};

    filteredConversations.forEach((conv: HistoryConversation) => {
      const localDate = new Date(conv.updated_at || conv.created_at)
        .toISOString()
        .slice(0, 10);
      if (!groups[localDate]) {
        groups[localDate] = [];
      }
      groups[localDate].push(conv);
    });

    return groups;
  };

  const conversationGroups = groupConversationsByDate();

  return (
    <>
      <div className="flex h-full min-w-0 flex-1 flex-col bg-k-canvas">
        <div className="mx-auto w-full max-w-6xl px-4 pb-4 pt-6 sm:px-6 md:px-12 md:py-6">
          <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-brand-indigo sm:text-4xl">
              {t('history.title')}
            </h1>

            {/* View Toggle */}
            <div className="inline-flex items-center gap-0 rounded-xl border-2 border-brand-indigo/15 bg-k-card p-1">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold transition-colors ${
                  viewMode === 'list'
                    ? 'bg-brand-indigo text-brand-cream'
                    : 'text-brand-indigo hover:bg-brand-indigo/10'
                }`}
              >
                <List className="h-4 w-4" />
                {t('history.list')}
              </button>
              <button
                type="button"
                onClick={() => setViewMode('visual')}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold transition-colors ${
                  viewMode === 'visual'
                    ? 'bg-brand-indigo text-brand-cream'
                    : 'text-brand-indigo hover:bg-brand-indigo/10'
                }`}
              >
                <LayoutGrid className="h-4 w-4" />
                {t('history.visual')}
              </button>
            </div>
          </div>

          <div className="relative mt-4">
            <Input
              placeholder={t('history.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 md:px-12">
            {conversationQuery.isLoading ? (
              <div className="space-y-4 py-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center space-x-4">
                    <Skeleton className="h-12 w-12 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-[250px]" />
                      <Skeleton className="h-4 w-[200px]" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center py-16 text-brand-indigo/70">
                <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-2xl bg-brand-teal-soft text-brand-indigo">
                  <MessageSquare className="h-9 w-9" strokeWidth={2} />
                </div>
                {searchQuery ? (
                  <>
                    <p className="mb-2 font-display text-xl font-bold text-brand-indigo">
                      {t('history.noMatchTitle')}
                    </p>
                    <p className="mb-4 text-sm">{t('history.noMatchHint')}</p>
                  </>
                ) : (
                  <>
                    <p className="mb-2 font-display text-xl font-bold text-brand-indigo">
                      {t('history.emptyTitle')}
                    </p>
                    <p className="mb-6 text-sm">
                      {t('history.emptyHint')}
                    </p>
                    <Button onClick={() => navigate('/home')} size="lg">
                      {t('history.startNewChat')}
                    </Button>
                  </>
                )}
              </div>
            ) : viewMode === 'visual' ? (
              // Visual Grid View
              <div className="grid gap-6 py-4 pb-48 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {filteredConversations.map((conversation) => (
                  <VisualCard
                    key={conversation.id}
                    conversation={conversation}
                    onDelete={(id) => deleteConversation.mutate(id)}
                    onRename={(_id, title) => {
                      setEditingConversation(conversation);
                      setNewTitle(title);
                      setOpen(true);
                    }}
                  />
                ))}
              </div>
            ) : (
              // List View (Original)
              <div className="space-y-8 py-4 pb-48">
                {Object.entries(conversationGroups).map(([date, convs]) => {
                  let dateString;
                  try {
                    const [year, month, day] = date.split('-').map(Number);
                    dateString = format(
                      new Date(year, month - 1, day),
                      t('history.dateFormat'),
                      { locale: dateFnsLocale },
                    );
                  } catch (error) {
                    console.error(error);
                  }

                  return (
                    <div key={date} className="space-y-2">
                      {dateString && (
                        <h2 className="px-1 py-2 text-xs font-bold uppercase tracking-[0.18em] text-brand-indigo/60">
                          {dateString}
                        </h2>
                      )}
                      <div className="space-y-2">
                        {convs.map((conversation) => (
                          <ConversationCard
                            key={conversation.id}
                            conversation={conversation}
                            onDelete={(id) => deleteConversation.mutate(id)}
                            onRename={(_id, title) => {
                              setEditingConversation(conversation);
                              setNewTitle(title);
                              setOpen(true);
                            }}
                            isEditing={!!editingConversation}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      <RenameDialogDrawer
        open={open}
        onOpenChange={handleOpenChange}
        newTitle={newTitle}
        onNewTitleChange={setNewTitle}
        onRename={handleRename}
      />

      <button
        type="button"
        aria-label={t('history.newProjectAria')}
        onClick={() => navigate('/home')}
        className="fixed bottom-6 right-6 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-brand-indigo text-brand-cream transition-colors active:bg-brand-indigo-hover mb-safe md:hidden"
      >
        <Plus className="h-7 w-7" strokeWidth={2.5} />
      </button>
    </>
  );
}
